import React, { useState } from "react";
import { CircularProgress, Snackbar, Box, SxProps } from "@mui/material";
import Alert from "@mui/material/Alert";
import { CheckCircle, Edit, Cancel } from "@mui/icons-material";

interface IProps {
  renderComponent: () => React.ReactElement;
  tableDataCellStyles?: SxProps;
  isEditable: boolean;
  isChanged: boolean;
  defaultValue: unknown | React.ReactElement;
  handleApply: () => void;
  isUpdating: boolean;
  alertErrorText?: string;
  isShowAlert: boolean;
  setIsShowAlert: (a: boolean) => void;
  isShowEditIcon?: boolean;
  ParentComponent?: React.ElementType;
  onCancel?: () => void;
}

const actionIconsStyles = {
  padding: "3px",
  "&:hover": { cursor: "pointer" },
};

const editIconStyles = {
  marginLeft: "5px",
  "&:hover": {
    color: "#1886cf",
    cursor: "pointer",
  },
};

const EditableContainerWithActions = ({
  isEditable,
  tableDataCellStyles,
  renderComponent,
  defaultValue,
  isChanged,
  handleApply,
  isUpdating,
  alertErrorText = "Error updating code",
  isShowAlert,
  setIsShowAlert,
  ParentComponent = "td",
  onCancel,
  isShowEditIcon = false,
}: IProps): React.ReactElement => {
  const [isEditing, setEditing] = useState(false);
  const handleClickActiveEdit = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isEditable) {
      setEditing(true);
    }
  };
  const handleCancel = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    e.stopPropagation();
    setEditing(false);
    onCancel && onCancel();
  };
  const onApply = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    e.stopPropagation();
    setEditing(false);
    handleApply();
  };
  const handleCloseAlert = () => setIsShowAlert(false);

  return (
    <Box
      component={ParentComponent}
      sx={{
        ...(isEditable &&
          !isEditing && {
            "&:hover": {
              cursor: "pointer !important",
              background: "rgba(128, 128, 128, 0.4) !important",
            },
          }),
        ...(tableDataCellStyles && tableDataCellStyles),
      }}
      onClick={handleClickActiveEdit}
    >
      <Box display="flex">
        {isUpdating && <CircularProgress size={30} />}
        {isUpdating ? null : isEditing ? (
          <Box display="flex" justifyContent="space-between" pt="2px" minWidth="225px" width="100%">
            {renderComponent()}
            <Box
              display="flex"
              alignItems="center"
              flexDirection="row"
              justifyContent="space-around"
            >
              {isChanged && (
                <CheckCircle sx={actionIconsStyles} onClick={onApply} color="primary" />
              )}
              <Cancel sx={actionIconsStyles} onClick={handleCancel} color="secondary" />
            </Box>
          </Box>
        ) : (
          <>
            {defaultValue as React.ReactElement}
            {isShowEditIcon && <Edit fontSize="small" sx={editIconStyles} />}
          </>
        )}
      </Box>

      <Snackbar open={isShowAlert} autoHideDuration={3000} onClose={handleCloseAlert}>
        <Alert onClose={handleCloseAlert} severity="error">
          {alertErrorText}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EditableContainerWithActions;
